import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, Bot, User, FileText, MapPin, Paperclip, Wand2, Sparkles, ShieldCheck, TrendingUp, BookOpen, Lightbulb, PlusCircle, AlertTriangle } from 'lucide-react';
import { api } from '../api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const MODES = [
  { 
    id: 'general', 
    name: 'General Assistant', 
    icon: Bot, 
    color: 'text-cyan-400', 
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
    desc: 'Ask anything about real estate.' 
  },
  { 
    id: 'brokerops', 
    name: 'BrokerOps Compliance', 
    icon: ShieldCheck, 
    color: 'text-emerald-400', 
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    desc: 'Strict compliance and policy guidance.',
    adminOnly: true
  },
  { 
    id: 'strategy', 
    name: 'Deal Strategy', 
    icon: Lightbulb, 
    color: 'text-amber-400', 
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    desc: 'Negotiation and offer structuring.' 
  },
  { 
    id: 'training', 
    name: 'Agent Training', 
    icon: BookOpen, 
    color: 'text-purple-400', 
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
    desc: 'Onboarding and process coaching.' 
  },
  { 
    id: 'market', 
    name: 'Market Intel', 
    icon: TrendingUp, 
    color: 'text-rose-400', 
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
    desc: 'Trends, reports, and recruiting data.',
    adminOnly: true
  }
];

const normalizeConflictWarning = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().replace(/\s+/g, ' ');
  const lowered = normalized.toLowerCase().replace(/[ .!?:;\-_]+$/g, '');
  const emptyVariants = new Set([
    'none',
    'no conflict',
    'no conflicts',
    'no contradiction',
    'no contradictions',
    'no conflicting information',
    'n/a',
    'na',
    'null'
  ]);
  return emptyVariants.has(lowered) ? null : normalized;
};

const ChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [citations, setCitations] = useState([]); 
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [stateContext, setStateContext] = useState('VA'); 
  const [currentMode, setCurrentMode] = useState('general');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [optimizing, setOptimizing] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const threadId = searchParams.get('thread_id');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
    }
  }, [messages, loading]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load Thread if ID exists
  useEffect(() => {
      if (threadId) {
          const loadThread = async () => {
              setLoading(true);
              try {
                  const res = await api.get(`/history/threads/${threadId}`);
                  const { thread, messages: msgs } = res.data;
                  setCurrentMode(thread.mode);
                  setMessages(msgs);
                  // Load citations from last message? Or accumulate?
                  // For simplicity, just use empty or last assistant's
                  const lastAssistant = msgs.slice().reverse().find(m => m.role === 'assistant');
                  if (lastAssistant?.citations) {
                      setCitations(lastAssistant.citations);
                  }
              } catch (e) {
                  toast.error("Failed to load thread");
                  navigate('/chat'); // Reset if invalid
              } finally {
                  setLoading(false);
              }
          };
          loadThread();
      } else {
          setMessages([]);
          setCitations([]);
      }
  }, [threadId, navigate]);

  // Fetch prompts when mode changes (only if no thread)
  useEffect(() => {
      if (threadId) return;
      
      const fetchPrompts = async () => {
          try {
              const res = await api.get(`/analytics/prompts?mode=${currentMode}`);
              setSuggestedPrompts(res.data.map(p => p.text));
          } catch (e) {
              setSuggestedPrompts([]);
          }
      };
      fetchPrompts();
  }, [currentMode, threadId]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;

    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/rag/query', { 
          message: userMsg.content,
          state: stateContext,
          mode: currentMode,
          thread_id: threadId
      });
      
      const assistantMsg = { 
        role: 'assistant', 
        content: response.data.answer,
        citations: response.data.citations,
        answer_source: response.data.answer_source,
        guidance_note: response.data.guidance_note,
      };
      setMessages(prev => [...prev, assistantMsg]);
      
      if (response.data.citations && response.data.citations.length > 0) {
        setCitations(response.data.citations);
      }
      
      // Show conflict warning if detected
      setConflictWarning(normalizeConflictWarning(response.data.conflict));
      
      // If new thread started, update URL
      if (!threadId && response.data.thread_id) {
          setSearchParams({ thread_id: response.data.thread_id });
      }
      
    } catch (error) {
      console.error("Chat error:", error);
      let errMsg = "Sorry, I encountered an error.";
      if (error.response?.status === 403) errMsg = "Access denied for this mode.";
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizePrompt = async () => {
    if (!input.trim()) {
        toast.error("Please type a draft prompt first.");
        return;
    }
    setOptimizing(true);
    try {
        const res = await api.post('/rag/optimize-prompt', { draft: input, state: stateContext });
        setInput(res.data.optimized_prompt);
        toast.success("Prompt optimized!");
    } catch (err) {
        console.error(err);
        toast.error("Failed to optimize prompt.");
    } finally {
        setOptimizing(false);
    }
  };

  const openCitations = (msgCitations) => {
    setCitations(msgCitations);
    setIsCitationPanelOpen(true);
  };

  const CitationsList = () => (
    <div className="space-y-3 p-4">
       {citations.map((cite, idx) => (
         <Card 
            key={idx} 
            className="overflow-hidden border border-white/10 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group" 
            onClick={() => setSelectedCitation(cite)} 
         >
           <div className="p-3 border-b border-white/5 text-[10px] font-mono text-cyan-400/80 uppercase tracking-wider flex justify-between items-center bg-black/20">
              <span className="truncate max-w-[180px]" title={cite.filename}>{cite.filename}</span>
              <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 h-4 text-[9px] px-1">Source</Badge>
           </div>
           <div className="p-3 text-xs text-white/70 line-clamp-4 leading-relaxed font-light group-hover:text-white transition-colors">
              {cite.text}
           </div>
         </Card>
       ))}
    </div>
  );

  const activeModeObj = MODES.find(m => m.id === currentMode) || MODES[0];

  const startNewChat = () => {
      setSearchParams({});
      setMessages([]);
      setCitations([]);
      setCurrentMode('general');
  };

  return (
    <div className="flex h-full relative gap-4">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full rounded-none md:rounded-3xl lg-glass overflow-hidden relative">
        {/* Chat Header */}
        <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-white/10 bg-white/5 backdrop-blur-md z-10">
          <div className="flex items-center gap-2 sm:gap-4 overflow-hidden">
             <div className="flex items-center gap-3">
                {threadId && (
                    <Button variant="ghost" size="icon" onClick={startNewChat} className="mr-[-8px] text-white/50 hover:text-white" title="New Chat">
                        <PlusCircle className="h-5 w-5" />
                    </Button>
                )}
                <div className={`h-8 w-8 rounded-full ${activeModeObj.bg} flex items-center justify-center border ${activeModeObj.border}`}>
                    <activeModeObj.icon className={`h-4 w-4 ${activeModeObj.color}`} />
                </div>
                <div>
                    <h2 className="text-sm font-semibold text-white tracking-wide truncate">
                        {threadId ? 'Continuing Conversation' : activeModeObj.name}
                    </h2>
                    <div className="flex items-center gap-1.5 mt-0.5">
                       <div className="h-1.5 w-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                       <span className="text-[10px] text-white/50 uppercase tracking-wider font-medium">Online ({stateContext})</span>
                    </div>
                </div>
             </div>
             
             {/* State Selector */}
             {!threadId && (
                 <div className="hidden sm:flex items-center gap-2 bg-white/5 border border-white/10 px-2 sm:px-3 py-1 rounded-full ml-4">
                    <MapPin className="h-3 w-3 text-cyan-400 shrink-0" />
                    <Select value={stateContext} onValueChange={setStateContext}>
                      <SelectTrigger className="h-5 border-0 bg-transparent shadow-none focus:ring-0 text-xs font-medium text-white/80 gap-1 p-0 w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="lg-glass-strong border-white/10 text-white">
                        <SelectItem value="VA">Virginia RE</SelectItem>
                        <SelectItem value="NC">North Carolina RE</SelectItem>
                        <SelectItem value="ALL">All States</SelectItem>
                      </SelectContent>
                    </Select>
                 </div>
             )}
          </div>
          
          {citations.length > 0 && (
             <Button 
               variant="ghost" 
               size="sm" 
               onClick={() => setIsCitationPanelOpen(true)}
               className="text-xs gap-2 bg-white/10 text-white hover:bg-white/20 whitespace-nowrap"
             >
               <FileText className="h-3.5 w-3.5" />
               <span className="hidden sm:inline">Evidence</span>
               <span className="sm:hidden">({citations.length})</span>
             </Button>
          )}
        </header>

        {/* Messages / Home State */}
        <ScrollArea className="flex-1 p-0" ref={scrollRef}>
          <div className="px-4 sm:px-10 py-8 space-y-8 pb-32 max-w-4xl mx-auto">
            {messages.length === 0 && !threadId ? (
              // Home Screen / Mode Selection
              <div className="animate-in fade-in duration-500">
                 <div className="text-center py-8 mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Welcome to AtCoastal.AI</h1>
                    <p className="text-white/50">Select an intelligence mode to begin.</p>
                 </div>
                 
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {MODES.filter(m => !m.adminOnly || user?.role === 'admin').map((mode) => {
                        const isLocked = mode.adminOnly && user?.role !== 'admin';
                        return (
                            <motion.button
                                key={mode.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => !isLocked && setCurrentMode(mode.id)}
                                className={`text-left p-5 rounded-2xl border transition-all relative overflow-hidden group
                                    ${currentMode === mode.id 
                                        ? 'bg-white/10 border-white/20 shadow-lg ring-1 ring-white/20' 
                                        : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10'
                                    }
                                    ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}
                                `}
                            >
                                <div className={`h-10 w-10 rounded-xl ${mode.bg} flex items-center justify-center border ${mode.border} mb-4`}>
                                    <mode.icon className={`h-5 w-5 ${mode.color}`} />
                                </div>
                                <h3 className="text-white font-medium mb-1">{mode.name}</h3>
                                <p className="text-xs text-white/50 leading-relaxed">{mode.desc}</p>
                                
                                {isLocked && (
                                    <div className="absolute top-3 right-3">
                                        <div className="bg-black/40 rounded-full p-1 border border-white/10">
                                            <ShieldCheck className="h-3 w-3 text-white/30" />
                                        </div>
                                    </div>
                                )}
                            </motion.button>
                        );
                    })}
                 </div>
                 
                 {/* Suggested Prompts for Current Mode */}
                 {suggestedPrompts.length > 0 && (
                     <div className="mt-12">
                        <h4 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-4 ml-1">Suggested for {activeModeObj.name}</h4>
                        <div className="grid grid-cols-1 gap-3">
                            {suggestedPrompts.map((p, i) => (
                                <button 
                                    key={i}
                                    onClick={() => setInput(p)}
                                    className="text-sm text-left p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all text-white/70 hover:text-white"
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                     </div>
                 )}
              </div>
            ) : (
              // Chat History
              messages.map((msg, idx) => (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 sm:gap-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className={`h-8 w-8 rounded-full ${activeModeObj.bg} border ${activeModeObj.border} flex items-center justify-center shrink-0 mt-1`}>
                      <activeModeObj.icon className={`h-4 w-4 ${activeModeObj.color}`} />
                    </div>
                  )}
                  
                  <div className={`max-w-[85%] sm:max-w-[75%]`}>
                     <div 
                        className={`px-4 sm:px-5 py-3 sm:py-4 text-sm leading-relaxed shadow-lg backdrop-blur-md ${
                          msg.role === 'user' 
                            ? 'bg-cyan-500/10 border border-cyan-500/20 text-white rounded-2xl rounded-br-sm' 
                            : 'bg-white/5 border border-white/10 text-white/90 rounded-2xl rounded-bl-sm'
                        }`}
                     >
                       <div className="whitespace-pre-wrap">{msg.content}</div>
                     </div>
                     
                     {msg.role === 'assistant' && msg.citations && msg.citations.length > 0 && (
                       <div className="mt-2 flex gap-2">
                          <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-[10px] gap-1.5 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/20 px-2 rounded-full border border-cyan-500/20"
                              onClick={() => openCitations(msg.citations)}
                          >
                              <FileText className="h-3 w-3" />
                              View {msg.citations.length} Sources
                          </Button>
                       </div>
                     )}

                     {msg.role === 'assistant' && msg.answer_source === 'external_llm' && msg.guidance_note && (
                       <div
                         className="mt-3 flex items-start gap-2 rounded-xl border border-cyan-500/20 bg-cyan-500/10 px-3 py-3 text-xs leading-relaxed text-cyan-100/90"
                         data-testid="external-llm-guidance-banner"
                       >
                         <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                         <div>
                           <span className="font-semibold text-cyan-200">External LLM Guidance: </span>
                           {msg.guidance_note.replace(/^External LLM Guidance:\s*/i, '')}
                         </div>
                       </div>
                     )}
                  </div>
                </motion.div>
              ))
            )}
            
            {loading && (
              <div className="flex gap-4 justify-start">
                 <div className={`h-8 w-8 rounded-full ${activeModeObj.bg} border ${activeModeObj.border} flex items-center justify-center shrink-0`}>
                    <activeModeObj.icon className={`h-4 w-4 ${activeModeObj.color}`} />
                 </div>
                 <div className="bg-white/5 border border-white/10 px-5 py-4 rounded-2xl rounded-bl-sm flex items-center justify-center">
                    <div className="flex gap-1.5">
                      <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 1 }} className="h-1.5 w-1.5 bg-white rounded-full" />
                      <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 1, delay: 0.2 }} className="h-1.5 w-1.5 bg-white rounded-full" />
                      <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 1, delay: 0.4 }} className="h-1.5 w-1.5 bg-white rounded-full" />
                    </div>
                 </div>
              </div>
            )}
            
            {/* Conflict Warning Banner */}
            {conflictWarning && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-200/90"
                data-testid="conflict-warning"
              >
                <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed">
                  <span className="font-semibold text-amber-300">Source Conflict Detected: </span>
                  {conflictWarning}
                </div>
              </motion.div>
            )}
          </div>
        </ScrollArea>

        {/* Floating Composer */}
        <div className="absolute bottom-4 sm:bottom-6 left-0 right-0 px-4 sm:px-10 z-20">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSend} className="relative group">
              <div className="relative bg-[#0b101b] border border-white/10 rounded-3xl p-2 pl-4 flex items-end gap-2 shadow-2xl ring-1 ring-white/5 focus-within:ring-cyan-500/50 transition-all">
                  <Button type="button" variant="ghost" size="icon" className="hidden sm:flex text-white/40 hover:text-white mb-1">
                     <Paperclip className="h-5 w-5" />
                  </Button>
                  
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={`Ask ${activeModeObj.name}...`}
                    className="flex-1 min-h-[44px] sm:min-h-[48px] max-h-32 py-3 bg-transparent border-0 focus-visible:ring-0 text-white placeholder:text-white/40 text-sm sm:text-base pr-20"
                    disabled={loading || optimizing}
                  />

                  {input.trim().length > 3 && !loading && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleOptimizePrompt}
                        disabled={optimizing}
                        className="absolute right-14 bottom-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/20 h-8 px-2 rounded-lg text-[10px] font-medium tracking-wide uppercase flex items-center gap-1.5 transition-all"
                        title="Improve using AI"
                      >
                        {optimizing ? <Sparkles className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                        <span className="hidden sm:inline">Improve</span>
                      </Button>
                  )}
                  
                  <Button 
                    type="submit" 
                    size="icon" 
                    className={`h-10 w-10 rounded-full mb-1 transition-all duration-300 ${input.trim() ? 'bg-cyan-500 text-black hover:bg-cyan-400' : 'bg-white/10 text-white/30'}`}
                    disabled={!input.trim() || loading}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Citations Panel */}
      {isMobile ? (
        <Sheet open={isCitationPanelOpen} onOpenChange={setIsCitationPanelOpen}>
            <SheetContent side="right" className="bg-[rgb(var(--bg-0))] border-l-white/10 p-0 w-full sm:w-[400px]">
                <SheetHeader className="px-4 py-4 border-b border-white/10">
                    <SheetTitle className="text-white text-base">Verified Sources</SheetTitle>
                </SheetHeader>
                <ScrollArea className="h-full pb-20">
                    <CitationsList />
                </ScrollArea>
            </SheetContent>
        </Sheet>
      ) : (
        isCitationPanelOpen && (
            <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="w-80 lg-glass rounded-3xl overflow-hidden flex flex-col shadow-2xl border border-white/10 z-20"
            >
            <div className="h-16 border-b border-white/10 flex items-center justify-between px-4 bg-white/5">
                <h3 className="font-semibold text-sm text-white">Verified Sources</h3>
                <Button variant="ghost" size="icon" onClick={() => setIsCitationPanelOpen(false)} className="h-8 w-8 text-white/50 hover:text-white">
                <span className="text-lg">×</span>
                </Button>
            </div>
            <ScrollArea className="flex-1">
                <CitationsList />
            </ScrollArea>
            </motion.div>
        )
      )}

      <Dialog open={!!selectedCitation} onOpenChange={(open) => !open && setSelectedCitation(null)}>
        <DialogContent className="lg-glass-strong border-white/10 text-white max-w-2xl w-[90vw] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm sm:text-base truncate text-cyan-400 pr-8">{selectedCitation?.filename}</DialogTitle>
            <DialogDescription className="text-white/50 text-xs">Full context from source document</DialogDescription>
          </DialogHeader>
          <ScrollArea className="mt-4 p-4 sm:p-6 bg-black/40 rounded-xl border border-white/5 h-[300px] sm:h-[400px]">
            <p className="text-xs sm:text-sm leading-7 sm:leading-8 whitespace-pre-wrap text-white/90 font-light">{selectedCitation?.text}</p>
          </ScrollArea>
          <div className="flex justify-end pt-4">
            <Button variant="secondary" onClick={() => setSelectedCitation(null)} className="bg-white/10 hover:bg-white/20 text-white border-0">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
